import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Header from '../components/ui/Header';
import AuthenticationGuard from '../components/ui/AuthenticationGuard';
import SmartLoadingBuffer from '../components/ui/SmartLoadingBuffer';
import Icon from '../components/AppIcon';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import useAuth from '../hooks/useAuth';
import { fetchProfile, updateProfile } from '../services/profileService';

const getInitialCadence = () => {
  if (typeof window === 'undefined') {
    return 'Daily';
  }
  return window.localStorage.getItem('cogito_preferred_cadence') || 'Daily';
};

const ProfilePage = () => {
  const { user, initializing, authPending, refreshAuthState } = useAuth();
  const [profile, setProfile] = useState(() => ({
    full_name: user?.full_name || '',
  }));
  const [isSaving, setIsSaving] = useState(false);
  const [isLoadingProfile, setIsLoadingProfile] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null);
  const [editor, setEditor] = useState(() => ({
    firstName: '',
    lastName: '',
    primaryEmail: user?.email || '',
    username: user?.username || '',
    preferredCadence: getInitialCadence(),
  }));
  const [sidebarBehavior, setSidebarBehavior] = useState('expand-on-hover');
  const [workspacePrefs, setWorkspacePrefs] = useState({
    commandMenu: true,
    assistantPanel: true,
    inlinePanel: false,
    telemetry: true,
  });

  useEffect(() => {
    const [first = '', ...rest] = (profile?.full_name || '').trim().split(/\s+/);
    const last = rest.join(' ');
    setEditor((prev) => ({
      ...prev,
      firstName: first,
      lastName: last,
      primaryEmail: user?.email || '',
      username: user?.username || '',
      preferredCadence: prev?.preferredCadence || getInitialCadence(),
    }));
  }, [profile?.full_name, user?.email, user?.username]);

  const initials = useMemo(() => {
    const source = profile?.full_name || user?.email || 'User';
    return source
      .split(' ')
      .map((part) => part?.charAt(0)?.toUpperCase())
      .slice(0, 2)
      .join('');
  }, [profile?.full_name, user?.email]);

  const accountHighlights = useMemo(
    () => [
      {
        icon: 'Clock',
        title: 'Member since',
        description: user?.created_at
          ? new Date(user.created_at).toLocaleDateString(undefined, {
              month: 'long',
              day: 'numeric',
              year: 'numeric',
            })
          : 'Recently joined',
      },
      {
        icon: 'Sparkles',
        title: 'Learning cadence',
        description: editor?.preferredCadence || 'Daily focus suggested',
      },
      {
        icon: 'Target',
        title: 'Focus streak',
        description: 'Build consistency to unlock streak insights.',
      },
    ],
    [editor?.preferredCadence, user?.created_at],
  );

  const accountIdentities = useMemo(
    () => [
      {
        provider: 'GitHub',
        icon: 'Github',
        email: user?.email,
        status: 'Connected',
      },
      {
        provider: 'Vercel Marketplace',
        icon: 'Shield',
        email: user?.email,
        status: 'Connected',
      },
    ],
    [user?.email],
  );

  const workspaceConnections = useMemo(
    () => [
      {
        provider: 'GitHub',
        description: 'Sync GitHub repos to Cogito for automatic branch planning.',
        icon: 'GitBranch',
        connected: true,
      },
      {
        provider: 'Slack',
        description: 'Pipe habit reminders directly to your team channels.',
        icon: 'Slack',
        connected: false,
      },
    ],
    [],
  );

  const handleEditorChange = useCallback((field) => (event) => {
    const value = event?.target?.value;
    setEditor((prev) => ({
      ...prev,
      [field]: value,
    }));
    setSaveStatus(null);
  }, []);

  const handlePreferenceToggle = useCallback((key) => {
    setWorkspacePrefs((prev) => ({
      ...prev,
      [key]: !prev?.[key],
    }));
  }, []);

  const handleResetEditor = useCallback(() => {
    const [first = '', ...rest] = (profile?.full_name || '').trim().split(/\s+/);
    const last = rest.join(' ');
    setEditor((prev) => ({
      ...prev,
      firstName: first,
      lastName: last,
      primaryEmail: user?.email || '',
      username: user?.username || '',
    }));
    setSaveStatus(null);
  }, [profile?.full_name, user?.email, user?.username]);

  const handleSidebarBehaviorChange = useCallback((event) => {
    setSidebarBehavior(event?.target?.value || 'expand-on-hover');
  }, []);

  const handleReloadProfile = useCallback(async () => {
    if (!user?.id) return;
    setIsLoadingProfile(true);
    try {
      const remoteProfile = await fetchProfile(user.id);
      if (remoteProfile) {
        setProfile({
          full_name: remoteProfile?.full_name || '',
        });
        const [first = '', ...rest] = (remoteProfile?.full_name || '').trim().split(/\s+/);
        const last = rest.join(' ');
        setEditor((prev) => ({
          ...prev,
          firstName: first,
          lastName: last,
          primaryEmail: user?.email || '',
          username: user?.username || '',
        }));
      }
      setSaveStatus({ type: 'success', message: 'Profile refreshed from workspace.' });
    } catch (error) {
      console.error('Failed to reload profile', error);
      setSaveStatus({ type: 'error', message: 'Could not refresh profile. Please retry.' });
    } finally {
      setIsLoadingProfile(false);
    }
  }, [user?.id]);

  const handleSaveProfile = useCallback(async () => {
    if (!user?.id) return;
    setIsSaving(true);
    setSaveStatus(null);

    try {
      const combinedName = `${editor?.firstName || ''} ${editor?.lastName || ''}`.trim() || null;
      const payload = {
        full_name: combinedName,
      };

      await updateProfile(user.id, payload);
      setProfile({
        full_name: combinedName || '',
      });
      if (typeof window !== 'undefined') {
        window.localStorage.setItem('cogito_preferred_cadence', editor?.preferredCadence || 'Daily');
      }
      await refreshAuthState?.();
      setSaveStatus({ type: 'success', message: 'Profile saved successfully.' });
    } catch (error) {
      console.error('Failed to update profile', error);
      setSaveStatus({ type: 'error', message: error?.message || 'Could not save profile.' });
    } finally {
      setIsSaving(false);
    }
  }, [editor, refreshAuthState, user?.id]);

  if (initializing || authPending) {
    return (
      <AuthenticationGuard isAuthenticated={true}>
        <div className="min-h-screen bg-background">
          <Header />
          <div className="pt-16">
            <SmartLoadingBuffer
              className="min-h-[60vh]"
              title="Loading your profile"
              message="We’re syncing your account details and personalized settings."
              hint="Cogito is preparing your profile overview."
            />
          </div>
        </div>
      </AuthenticationGuard>
    );
  }

  return (
    <AuthenticationGuard isAuthenticated={true}>
      <div className="min-h-screen bg-background">
        <Header />

        <main className="pt-16">
          <div className="max-w-5xl mx-auto px-4 lg:px-8 py-8 space-y-8">
            <section className="bg-card border border-border rounded-2xl p-6 sm:p-8 flex flex-col gap-6">
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
                <div className="flex items-start space-x-4">
                  <div className="relative">
                    <div className="absolute inset-0 rounded-2xl bg-primary/30 blur-2xl opacity-70" aria-hidden />
                    <div className="relative w-20 h-20 rounded-2xl bg-primary/10 border border-primary/20 flex items-center justify-center text-3xl font-semibold text-primary">
                      {initials}
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center space-x-3">
                      <h1 className="text-3xl font-semibold text-foreground tracking-tight">{profile?.full_name || `${editor?.firstName || 'Your'} ${editor?.lastName || 'profile'}`.trim()}</h1>
                      <span className="px-2.5 py-1 rounded-full bg-primary/10 text-xs font-medium text-primary">Smart learner</span>
                    </div>
                    <p className="text-sm text-muted-foreground flex items-center space-x-2">
                      <Icon name="Mail" size={14} />
                      <span>{user?.email}</span>
                    </p>
                    <p className="text-sm text-muted-foreground leading-relaxed max-w-2xl">
                      Cogito uses your routines to surface the right nudges, briefings, and progress trends. Tweak your cadence and explore the panels below to keep your workspace aligned with your learning style.
                    </p>
                    <div className="flex items-center space-x-2 text-xs text-muted-foreground">
                      <span className="inline-flex items-center space-x-1">
                        <Icon name="Sparkles" size={14} className="text-primary" />
                        <span>Focus roadmap active</span>
                      </span>
                      <span>•</span>
                      <span>Next milestone briefing in 2 days</span>
                    </div>
                  </div>
                </div>
                <div className="hidden sm:flex sm:items-center sm:space-x-3" />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {accountHighlights.map((highlight) => (
                  <div
                    key={highlight.title}
                    className="rounded-xl border border-border/60 bg-muted/30 p-4 space-y-2"
                  >
                    <div className="flex items-center space-x-2 text-sm font-medium text-foreground">
                      <Icon name={highlight.icon} size={16} className="text-primary" />
                      <span>{highlight.title}</span>
                    </div>
                    <p className="text-xs text-muted-foreground leading-snug">{highlight.description}</p>
                  </div>
                ))}
              </div>
            </section>

            {saveStatus && (
              <div
                className={`rounded-xl border px-4 py-3 text-sm ${
                  saveStatus?.type === 'error'
                    ? 'border-destructive/50 bg-destructive/10 text-destructive'
                    : 'border-success/40 bg-success/10 text-success'
                }`}
              >
                {saveStatus?.message}
              </div>
            )}

            <section className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 bg-card rounded-2xl border border-border p-6 space-y-6">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                    <Icon name="Contact" size={18} className="text-primary" />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-foreground">Profile preferences</h2>
                    <p className="text-sm text-muted-foreground">Adjust how Cogito addresses you and syncs your rhythm.</p>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Input
                    label="First name"
                    placeholder="Add how you want to be addressed"
                    value={editor?.firstName}
                    onChange={handleEditorChange('firstName')}
                  />
                  <Input
                    label="Last name"
                    placeholder="Add your surname"
                    value={editor?.lastName}
                    onChange={handleEditorChange('lastName')}
                  />
                  <Input
                    label="Primary email"
                    value={editor?.primaryEmail}
                    disabled
                    className="bg-muted/40"
                    description="Primary email is used for account notifications."
                  />
                  <Input
                    label="Username"
                    placeholder="Displayed name across Cogito"
                    value={editor?.username}
                    disabled
                    className="bg-muted/40"
                    description="Username appears wherever a short identifier is needed."
                  />
                  <div className="space-y-2 sm:col-span-2">
                    <label className="text-sm font-medium text-foreground">Preferred cadence</label>
                    <select
                      className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground"
                      value={editor?.preferredCadence}
                      onChange={handleEditorChange('preferredCadence')}
                    >
                      <option value="Daily">Daily</option>
                      <option value="Weekly">Weekly</option>
                      <option value="Biweekly">Biweekly</option>
                      <option value="Monthly">Monthly</option>
                    </select>
                    <p className="text-xs text-muted-foreground">Your nudges and dashboards adapt to this rhythm.</p>
                  </div>
                </div>
                <div className="flex items-center justify-end space-x-3 pt-4 border-t border-border/60">
                  <Button variant="ghost" onClick={handleResetEditor} disabled={isSaving || isLoadingProfile}>
                    Cancel
                  </Button>
                  <Button
                    variant="default"
                    iconName="Save"
                    onClick={handleSaveProfile}
                    loading={isSaving}
                    disabled={isSaving}
                  >
                    Save changes
                  </Button>
                </div>
                <div className="rounded-xl border border-dashed border-border/70 p-4 bg-muted/20">
                  <h3 className="text-sm font-semibold text-foreground mb-2">Workspace snapshot</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs text-muted-foreground">
                    <div className="rounded-lg bg-card/80 border border-border/60 p-3 space-y-1">
                      <span className="uppercase tracking-wide text-muted-foreground/80">Focus level</span>
                      <p className="text-lg font-semibold text-foreground">Balanced</p>
                      <p>Cadence is aligned with current routines.</p>
                    </div>
                    <div className="rounded-lg bg-card/80 border border-border/60 p-3 space-y-1">
                      <span className="uppercase tracking-wide text-muted-foreground/80">Momentum</span>
                      <p className="text-lg font-semibold text-foreground">Building</p>
                      <p>Keep logging habits for trend unlocks.</p>
                    </div>
                    <div className="rounded-lg bg-card/80 border border-border/60 p-3 space-y-1">
                      <span className="uppercase tracking-wide text-muted-foreground/80">Next action</span>
                      <p className="text-lg font-semibold text-primary">Review flashcards</p>
                      <p>10 cards queued for spaced recall.</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-card rounded-2xl border border-border p-6 space-y-5">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-success/10 rounded-lg flex items-center justify-center">
                    <Icon name="Shield" size={18} className="text-success" />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-foreground">Security & health</h2>
                    <p className="text-sm text-muted-foreground">Keep your workspace safeguarded and proactive.</p>
                  </div>
                </div>
                <div className="space-y-3 text-sm text-muted-foreground">
                  <div className="flex items-center justify-between">
                    <span>Account email</span>
                    <span className="text-foreground font-medium">{user?.email}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span>Verification status</span>
                    <span className="text-foreground font-medium">{user?.email_confirmed_at ? 'Verified' : 'Pending confirmation'}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span>Last sync</span>
                    <span className="text-foreground font-medium">{new Date().toLocaleTimeString()}</span>
                  </div>
                </div>
                <div className="border-t border-border pt-4 space-y-3 text-sm text-muted-foreground">
                  <p className="font-medium text-foreground">Recommended actions</p>
                  <ul className="space-y-3">
                    <li className="flex items-start space-x-2">
                      <Icon name="Key" size={16} className="mt-0.5 text-primary" />
                      <span>Enable two-factor authentication to safeguard your account.</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <Icon name="RefreshCw" size={16} className="mt-0.5 text-warning" />
                      <span>Rotate your password every few months and avoid reusing it elsewhere.</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <Icon name="Bell" size={16} className="mt-0.5 text-success" />
                      <span>Turn on email alerts for new logins in your account settings.</span>
                    </li>
                  </ul>
                  <Button
                    variant="ghost"
                    iconName="ExternalLink"
                    className="justify-start px-0"
                    onClick={() => (window.location.href = '/settings#privacy')}
                  >
                    Manage security in settings
                  </Button>
                </div>
              </div>
            </section>

            <section className="bg-card border border-border rounded-2xl p-6 space-y-6">
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center">
                    <Icon name="LayoutPanelLeft" size={18} className="text-primary" />
                  </div>
                  <div className="flex-1">
                    <h2 className="text-lg font-semibold text-foreground">Sidebar behavior</h2>
                    <p className="text-sm text-muted-foreground">Choose your preferred sidebar behavior: open, closed, or expand on hover.</p>
                  </div>
                  <select
                    value={sidebarBehavior}
                    onChange={handleSidebarBehaviorChange}
                    className="px-3 py-2 border border-border rounded-lg bg-background text-sm"
                  >
                    <option value="open">Open</option>
                    <option value="closed">Closed</option>
                    <option value="expand-on-hover">Expand on hover</option>
                  </select>
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 rounded-lg bg-success/10 flex items-center justify-center">
                    <Icon name="BarChart" size={18} className="text-success" />
                  </div>
                  <div className="flex-1">
                    <h2 className="text-lg font-semibold text-foreground">Analytics & marketing</h2>
                    <p className="text-sm text-muted-foreground">Help improve Cogito by sharing anonymous usage patterns.</p>
                  </div>
                  <button
                    onClick={() => handlePreferenceToggle('telemetry')}
                    className={`w-14 h-7 rounded-full transition flex items-center px-1 ${
                      workspacePrefs?.telemetry ? 'bg-success text-success-foreground' : 'bg-muted text-muted-foreground'
                    }`}
                    aria-pressed={workspacePrefs?.telemetry}
                  >
                    <span
                      className={`w-5 h-5 rounded-full bg-background shadow transform transition ${
                        workspacePrefs?.telemetry ? 'translate-x-7' : ''
                      }`}
                    />
                  </button>
                </div>
              </div>
            </section>

            <section className="bg-error/10 border border-error/40 rounded-2xl p-6 space-y-4">
              <div className="flex items-start space-x-3">
                <div className="w-10 h-10 rounded-full bg-error text-error-foreground flex items-center justify-center">
                  <Icon name="AlertTriangle" size={18} />
                </div>
                <div className="space-y-2">
                  <h2 className="text-lg font-semibold text-error-foreground">Danger zone</h2>
                  <p className="text-sm text-error-foreground/80">
                    Deleting your account is permanent and cannot be undone. Your data will be removed within 30 days, except where retention is required by law.
                  </p>
                  <Button variant="danger" size="sm" iconName="Trash2">
                    Request account deletion
                  </Button>
                </div>
              </div>
            </section>
          </div>
        </main>
      </div>
    </AuthenticationGuard>
  );
};

export default ProfilePage;
